import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "~/v2/api/queryKeys";

interface ManifestResponse {
  symbols?: string[];
  dark?: string[];
}

interface ManifestData {
  availableSymbols: Set<string>;
  symbolsWithDark: Set<string>;
}

async function fetchManifest(baseUrl: string): Promise<ManifestData> {
  const res = await fetch(`${baseUrl}/manifest.json`);
  const data: ManifestResponse = await res.json();
  return {
    availableSymbols: new Set(data.symbols ?? []),
    symbolsWithDark: new Set(data.dark ?? []),
  };
}

/** Fetches and caches the asset logo manifest for the session. */
export function useAssetLogoManifest(): {
  availableSymbols: Set<string> | null;
  symbolsWithDark: Set<string> | null;
  isLoading: boolean;
} {
  const baseUrl =
    typeof window !== "undefined" ? window.env?.ASSET_LOGO_BASE_URL : undefined;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.assets.logoManifest(baseUrl || ""),
    queryFn: () => {
      if (!baseUrl) {
        throw new Error("ASSET_LOGO_BASE_URL required");
      }
      return fetchManifest(baseUrl);
    },
    enabled: !!baseUrl,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return {
    availableSymbols: data?.availableSymbols ?? null,
    symbolsWithDark: data?.symbolsWithDark ?? null,
    isLoading,
  };
}
